import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { getErrorMessage } from '@/api/api-client'
import { changePassword } from '@/api/auth/auth.api'
import { AppDialog } from '@/components/AppDialog'
import { PasswordInput } from '@/components/PasswordInput'
import { Button } from '@/components/ui/button'

type PasswordFormData = {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

const defaultValues: PasswordFormData = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
}

interface ChangePasswordDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export default function ChangePasswordDialog({ isOpen, onOpenChange }: ChangePasswordDialogProps) {
  const { t } = useTranslation()
  const [error, setError] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<PasswordFormData>({
    defaultValues,
  })

  const newPassword = watch('newPassword')

  const onSubmit = async (data: PasswordFormData) => {
    setError('')

    if (data.newPassword !== data.confirmPassword) {
      setError(t('profile.passwordsMismatch'))
      return
    }

    if (data.newPassword.length < 8) {
      setError(t('profile.passwordMinLength'))
      return
    }

    if (!/^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/.test(data.newPassword)) {
      setError(t('profile.invalidPassword'))
      return
    }

    try {
      setIsLoading(true)
      await changePassword(data.currentPassword, data.newPassword)
      onOpenChange(false)
      reset(defaultValues)
    } catch (err: unknown) {
      setError(getErrorMessage(err) || t('profile.passwordUpdateError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset(defaultValues)
    setError('')
    onOpenChange(false)
  }

  return (
    <AppDialog isOpen={isOpen} onOpenChange={handleClose} title={t('profile.changePasswordTitle')} dialogContentClassName='pt-4'>
      <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col items-end gap-8'>
        <div className='flex w-full flex-col items-end gap-[21px]'>
          <Controller
            name='currentPassword'
            control={control}
            rules={{ required: t('profile.currentPasswordRequired') }}
            render={({ field }) => (
              <PasswordInput
                id='current-password'
                label={t('profile.currentPassword')}
                {...field}
                error={!!errors.currentPassword}
                supportingText={errors.currentPassword?.message}
                required
              />
            )}
          />
          <Controller
            name='newPassword'
            control={control}
            rules={{
              required: t('profile.newPasswordRequired'),
              minLength: { value: 8, message: t('profile.passwordMinLength') },
            }}
            render={({ field }) => (
              <PasswordInput
                id='new-password'
                label={t('profile.newPassword')}
                {...field}
                error={!!errors.newPassword}
                supportingText={errors.newPassword?.message}
                required
              />
            )}
          />
          <Controller
            name='confirmPassword'
            control={control}
            rules={{
              required: t('profile.confirmPasswordRequired'),
              validate: value => value === newPassword || t('profile.passwordsMismatch'),
            }}
            render={({ field }) => (
              <PasswordInput
                id='confirm-password'
                label={t('profile.confirmNewPassword')}
                {...field}
                error={!!errors.confirmPassword}
                supportingText={errors.confirmPassword?.message}
                required
              />
            )}
          />
          {error && <div className='text-destructive w-full text-end text-sm'>{error}</div>}
        </div>
        <div className='flex w-full flex-col items-center gap-2'>
          <Button
            type='submit'
            disabled={isLoading}
            className='h-12 w-full max-w-[335px] rounded-full bg-[#FAD600] font-medium text-[#333333] hover:bg-[#FAD600]/90'
          >
            {isLoading ? t('profile.saving') : t('profile.saveNewPassword')}
          </Button>
        </div>
      </form>
    </AppDialog>
  )
}
